"use client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react";
import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";

export function PostHogProvider({
  apiToken,
  cspNonce,
  children,
}: {
  apiToken: string;
  cspNonce: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    posthog.init(apiToken, {
      api_host: "/ingest",
      ui_host: "https://eu.posthog.com",
      capture_pageview: "history_change",
      capture_pageleave: true,
      capture_exceptions: true,
      debug: process.env.NODE_ENV !== "production",
      prepare_external_dependency_script: (script) => {
        script.nonce = cspNonce;
        return script;
      },
      prepare_external_dependency_stylesheet: (stylesheet) => {
        stylesheet.nonce = cspNonce;
        return stylesheet;
      },
    });
  }, []);

  return (
    <PHProvider client={posthog}>
      <SuspendedPostHogPageView />
      {children}
    </PHProvider>
  );
}

function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();

  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname;
      const search = searchParams.toString();
      if (search) {
        url += "?" + search;
      }
      posthog.capture("$pageview", { $current_url: url });
    }
  }, [pathname, searchParams, posthog]);

  return null;
}

function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}
